/**
 * Based on this code
 * wdpr-ui-angular-mapplication/src/map/mapplication/mapplication.directive.js
 */
import { AfterViewInit, Component, Input, OnInit, NgZone, EventEmitter, Output, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';

import { ConfigService } from '@finder/core/config.service';
import { MapDefaultsService } from './defaults/map.defaults.service';
import { MapFactoryService } from './map/map.factory.service';
import { MapDataLocationMarker, MapInterface, MarkerOptions } from '@finder/shared/components/map/map/map.interface';
import { MAP_DEFAULT_ID } from '@finder/shared/components/map/defaults/map.defaults.constants';
import { ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { MouseIntentService } from '@finder/shared/services/mouse-intent/mouse-intent.service';
import { MouseIntentOptions } from '@finder/shared/services/mouse-intent/mouse-intent.interface';

@Component({
    selector: 'finder-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() public title: string;
    // Map data
    @Input() public defaults: any = {};
    @Input() public mapBrand: string;
    @Input() public maxBounds;
    // @Input() public tiles;
    // @Input() public layers;
    @Input() public width: string;
    @Input() public height: string;
    @Input() public center: MapDataLocationMarker;
    @Input() public markers: Array<MapDataLocationMarker> = [];
    @Input() public paradeRoute: Array<any>;
    @Input() public mapId = MAP_DEFAULT_ID;
    @Input() public markerOptions: MarkerOptions = { showPopup: false, keyboard: false };
    @Output() public mapIsReady: EventEmitter<MapInterface> = new EventEmitter();
    @Output() public pinsAreDropped: EventEmitter<MapInterface> = new EventEmitter();

    @ViewChild('map', { read: ElementRef, static: true }) el: ElementRef;

    environment: string;
    private CLASS_NAME = '[FINDER-MAP] - ';

    private map: MapInterface;
    private defaultMapValues;
    private siteId: string;

    constructor(
        private configService: ConfigService,
        private defaultService: MapDefaultsService,
        private mapFactory: MapFactoryService,
        private loggerService: LoggerService,
        private lazyLoader: LazyLoaderService,
        private viewport: ViewportDetection,
        private zone: NgZone,
        private mouseIntent: MouseIntentService,
    ) {
        this.environment = this.configService.getValue('environment');
    }


    /**
     * Base the init code on this
     * wdpr-ui-angular-mapplication/src/map/mapplication/mapplication.directive.js
     */
    ngOnInit() {
        if (this.configService.isDevelopment()) {
            this.lazyLoader.lazyLoadCssResource('finder-leaflet.css');
        }

        // This is injected from the server vars. It's how we get 'wdw' and can pull in the proper map vars.
        this.siteId = this.configService.getValue('siteId');

        // Setup map intent - we will assume that is we want the scroll wheel to be enabled
        // that we should add the "intent" capabilities. We need to first disable the scroll
        // zoom (so the map doesn't set it up) and then the intent service will add all the
        // event handling for over/out/intent.
        if (this.defaults.scrollWheelZoom === true) {
            this.defaults.scrollWheelZoom = false;
            this.bindIntent();
        }

        this.defaultMapValues = this.defaultService.setDefaults(this.defaults, this.siteId, this.mapBrand);

        // Check to make sure values passed in are properly formatted
        this.updateHeight();
        this.updateWidth();
    }

    /**
     * We wait until the DOM has been updated with the correct inline css values before we create the map
     */
    ngAfterViewInit(): void {
        /**
         * There was an issue where the page would redraw when the mouse moved on the screen. This was due
         * to zone.js listening for any changes from events. This fixes that issue by moving leaflet
         * outside of zone.js so mouse movements are contained to the plugin
         */
        this.zone.runOutsideAngular(() => this.setupMap());
    }

    ngOnDestroy(): void {
        // If a bad map type is passed in the object won't exist
        if (this.map) {
            this.map.cleanup();
        }
    }

    /**
     * Redraw the map
     */
    redrawMap() {
        this.map.redrawMap();
    }

    /**
     * Create the map
     */
    private setupMap() {
        const mapDefaultOptions = this.defaultService.getMapCreationDefaults();
        mapDefaultOptions.keyboard = this.markerOptions.keyboard;
        // Create the map
        this.map = this.mapFactory.createMap(
            this.mapId,
            mapDefaultOptions,
            this.defaultMapValues.mapType
        );

        if (!this.map) {
            this.loggerService.error(`${this.CLASS_NAME} can not create map of type "${this.defaultMapValues.mapType}"`);

            return;
        }

        // Easy way to return a reference to the map
        this.map.mapIsReady().subscribe(mapObj => {
            // The check for parade happens in the map class
            if (this.paradeRoute) {
                this.map.focusOnParadeRoute();
            } else if (this.markers && this.markers.length > 0) {
                this.map.focusOnPins(this.markerOptions);
            }
            // Let others know we're ready
            this.mapIsReady.emit(mapObj);
        });

        this.map.pinsAreDropped().subscribe(mapObj => {
            this.pinsAreDropped.emit(mapObj);
        });

        // To allow the guest to scroll on iOS by tapping and dragging on the map
        if (this.viewport.isMobile()) {
            this.map.removeTapControl();
        }

        // Setup the map attributes
        this.checkMapAttributes();

        // Place the markers on the map
        if (Array.isArray(this.markers) && this.markers.length > 0) {
            this.map.setMarkers(this.markers, this.markerOptions);
        }

        // Check for parade route
        if (this.paradeRoute && this.paradeRoute.length > 0) {
            this.map.setParadeRoute(this.paradeRoute);
        }
    }

    /**
     * Check the width formatting for the map
     */
    private updateWidth() {
        // Check for vw, px if the width doesn't end with anything
        if (this.width && (this.width.endsWith('px') || this.width.endsWith('vw'))) {
            return;
        }
        this.width += 'px';
    }

    /**
     * Check the height formatting for the map
     */
    private updateHeight() {
        // Check for vh, px if the height doesn't end with anything
        if (this.height && (this.height.endsWith('px') || this.height.endsWith('vh'))) {
            return;
        }
        this.height += 'px';
    }

    /**
     * Check attributes
     */
    private checkMapAttributes() {
        // Get center values
        let defaultCenter = this.defaultMapValues.center;
        if (this.center) {
            defaultCenter = this.center;
        }

        // Set view
        this.map.setView(defaultCenter.lat, defaultCenter.lng, defaultCenter.zoom);

        // Set expand controls
        if (this.defaultMapValues.addExpandControl) {
            this.map.addExpandControl(this.defaultMapValues.addExpandControl);
        }

        // Add recenter controls
        if (this.defaultMapValues.addRecenterControl) {
            this.map.addRecenterControl(this.defaultMapValues.addRecenterControl);
        }

        // Set zoom controls (should come after other controls are set)
        if (this.defaultMapValues.addZoomControl) {
            this.map.setZoomControls(this.defaultMapValues.zoomOptions);
        }

        // Check for bounds
        if (this.maxBounds) {
            this.map.setMaxBounds(this.maxBounds.southWest, this.maxBounds.northEast);
        } else {
            this.map.setMaxBounds(this.defaultMapValues.maxBounds.southWest, this.defaultMapValues.maxBounds.northEast);
        }

        // ATM we don't need to check for tiles and layers passed into the component
        this.map.setTileLayers(this.defaultMapValues.tileLayer, this.defaultMapValues.tileLayerOptions);

        // Check for custom tiles
        // if (!this.tiles && !this.layers) {

        /**
         * @TODO
         * The original code is using a promise to set the map tiles. I think this is so getters/setters
         * properly respond to the request if there are multiple maps on the page.
         *
         * wdpr-ui-angular-mapplication/src/map/mapplication-data.factory.js
         * This entire class is used to store data for later retrival. It uses promise to make sure the set/get don't
         * collide.
         */
        // }

        // @TODO broadcast events from the map
        // let logic = 'broadcast';
        // let eventName;
    }

    private bindIntent() {
        const options: MouseIntentOptions = {
            // The element to bind the listeners to
            element: this.el,
            // The length of time to delay before intent is fired
            duration: this.configService.getValue('mouseIntentDuration'),
            // What to execute when intent is fired
            mouseIntentCallback: () => {
                this.map.enableScrollWheelZoom();
            },
            // What to execute when out is fired
            mouseOutCallback: () => {
                this.map.disableScrollWheelZoom();
            },
        };

        // Pass the options in so the magic can happen!
        this.mouseIntent.bind(options);
    }
}
